import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Bell, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";

export default function CampaignModal({ open, onOpenChange, campaign, onSaved }) {
  const isEdit = Boolean(campaign);
  const { isPro } = useBilling();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [shortcode, setShortcode] = useState("");
  const [useCustomCode, setUseCustomCode] = useState(false);
  const [folder, setFolder] = useState("");
  const [folders, setFolders] = useState([]);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(20);
  const [alertWindow, setAlertWindow] = useState(1);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setName(campaign?.name || "");
      setUrl(campaign?.destination_url || "");
      setShortcode(campaign?.shortcode || "");
      setUseCustomCode(Boolean(campaign));
      setFolder(campaign?.folder || "");
      setAlertEnabled(Boolean(campaign?.alert_enabled));
      setAlertThreshold(campaign?.alert_threshold || 20);
      setAlertWindow(campaign?.alert_window_hours || 1);
      setErr("");
      // Load existing folder names for autocomplete
      api.get("/folders").then(({ data }) => setFolders(data || [])).catch(() => {});
    }
  }, [open, campaign]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const patch = { name, destination_url: url, folder: folder.trim() };
        if (useCustomCode && shortcode && shortcode !== campaign.shortcode) {
          patch.shortcode = shortcode.trim();
        }
        if (isPro) {
          patch.alert_enabled = alertEnabled;
          patch.alert_threshold = alertThreshold;
          patch.alert_window_hours = alertWindow;
        }
        const { data } = await api.patch(`/campaigns/${campaign.id}`, patch);
        saved = data;
        toast.success("Campaign updated");
      } else {
        const body = { name, destination_url: url };
        if (useCustomCode && shortcode.trim()) {
          body.shortcode = shortcode.trim();
        }
        if (folder.trim()) body.folder = folder.trim();
        const { data } = await api.post(`/campaigns`, body);
        saved = data;
        toast.success("Campaign created");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="campaign-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {isEdit ? "Edit campaign" : "New campaign"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Change the name or update the destination URL. Your QR code stays the same."
              : "Give your campaign a name and where it should send visitors."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Campaign name
            </Label>
            <Input
              data-testid="modal-campaign-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Autumn menu insert"
              required
              className="h-11 rounded-xl"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Destination URL
            </Label>
            <Input
              data-testid="modal-destination-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              type="url"
              placeholder="https://example.com/landing"
              required
              className="h-11 rounded-xl font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              Must start with https:// or http://.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Folder <span className="text-slate-400 normal-case font-normal">(optional)</span>
            </Label>
            <Input
              data-testid="modal-folder-input"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              placeholder="e.g. Clients / Acme, Q4 Print, Retail"
              list="folder-suggestions"
              maxLength={60}
              className="h-11 rounded-xl"
            />
            <datalist id="folder-suggestions">
              {folders.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
            <p className="text-xs text-slate-500 mt-1.5">
              Group campaigns by client, channel, or anything you like. Leave blank for uncategorised.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-start gap-3">
              <input
                id="use-custom-shortcode"
                type="checkbox"
                checked={useCustomCode}
                onChange={(e) => setUseCustomCode(e.target.checked)}
                data-testid="modal-use-custom-shortcode-toggle"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="use-custom-shortcode" className="flex-1 cursor-pointer">
                <div className="text-sm font-semibold text-slate-900">Use a vanity short link</div>
                <div className="text-xs text-slate-500">
                  Pick a branded shortcode like <code className="font-mono">summer-menu</code> instead of a random one.
                </div>
              </label>
            </div>
            {useCustomCode && (
              <div className="mt-3">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                  Shortcode
                </Label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-500 font-mono select-none">/api/r/</span>
                  <Input
                    data-testid="modal-shortcode-input"
                    value={shortcode}
                    onChange={(e) => setShortcode(e.target.value)}
                    placeholder="summer-menu"
                    className="h-10 rounded-lg font-mono text-sm flex-1"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  3-32 characters — letters, numbers, dashes, underscores. Must be unique.
                </p>
              </div>
            )}
          </div>

          {isEdit && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="modal-alerts-section">
              <div className="flex items-start gap-3">
                <input
                  id="alert-enabled"
                  type="checkbox"
                  checked={alertEnabled}
                  onChange={(e) => setAlertEnabled(e.target.checked)}
                  disabled={!isPro}
                  data-testid="modal-alert-enabled-toggle"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                />
                <label htmlFor="alert-enabled" className="flex-1 cursor-pointer">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-500" />
                    Notify me on scan spikes
                    {!isPro && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-black uppercase tracking-wider border border-amber-200">
                        <Lock className="w-2.5 h-2.5" /> PRO
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    Get a bell + optional email when scans in a window exceed your threshold.
                  </div>
                </label>
              </div>
              {alertEnabled && isPro && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                      Threshold
                    </Label>
                    <Input
                      data-testid="modal-alert-threshold-input"
                      type="number"
                      min={1}
                      value={alertThreshold}
                      onChange={(e) => setAlertThreshold(parseInt(e.target.value, 10) || 1)}
                      className="h-10 rounded-lg font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                      Window (hours)
                    </Label>
                    <Input
                      data-testid="modal-alert-window-input"
                      type="number"
                      min={1}
                      max={72}
                      value={alertWindow}
                      onChange={(e) => setAlertWindow(parseInt(e.target.value, 10) || 1)}
                      className="h-10 rounded-lg font-mono"
                    />
                  </div>
                </div>
              )}
              {!isPro && (
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                  data-testid="modal-alerts-upgrade-cta"
                >
                  <Sparkles className="w-3 h-3" />
                  Upgrade to unlock scan alerts
                </Link>
              )}
            </div>
          )}

          {err && (
            <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg">
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl"
              data-testid="modal-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              data-testid="modal-save-campaign-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEdit ? (
                "Save changes"
              ) : (
                "Create campaign"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
